import {
  ConcreteExperienceMessage,
  IExperience
} from '@dadohr/shared/domainTypes/experience';
import {
  AssigneeBouncedErrorData,
  ErroDataType
} from '@dadohr/shared/domainTypes/instance';

/**
 * SC-33594 core — pure matcher for the bounce-Retry fix. Kept in its own,
 * import-light module (no firebase/service deps) so it can be unit-tested
 * without pulling in the whole instance-service dependency chain.
 *
 * Flips `ignore = true` on every non-ignored ASSIGNEE_BOUNCED entry matching
 * the same recipient email + timeline item, and returns how many it cleared.
 * We clear ALL matches, not just one: a single bounced send can be recorded as
 * several duplicate rows (one per redelivered Postmark bounce webhook), and one
 * successful resend resolves deliverability for all of them. `itemTplId` is
 * compared against whatever the bounce entry stored (message tpl id for
 * MESSAGE, task instance id for TASK) — the retry passes the issue's own
 * `itemTplId`, so it's the same stored value on both sides.
 */
export const markMatchingAssigneeBouncesIgnored = (
  instanceErrors: IExperience['instanceErrors'],
  match: { itemType: 'MESSAGE' | 'TASK'; itemTplId: string; email: string }
): number => {
  let cleared = 0;
  instanceErrors.forEach((iErr) => {
    if (iErr.type !== ErroDataType.ASSIGNEE_BOUNCED || iErr.ignore) {
      return;
    }
    const bounce = iErr as AssigneeBouncedErrorData;
    if (
      bounce.itemType === match.itemType &&
      bounce.itemTplId === match.itemTplId &&
      bounce.email === match.email
    ) {
      iErr.ignore = true;
      cleared++;
    }
  });
  return cleared;
};

/**
 * SC-33594 (double-count) — one bounced send is written to BOTH
 * `messages[].status='BOUNCED'` (→ a generic SENT_ITEM row) and an
 * `ASSIGNEE_BOUNCED` instanceError (→ a bounce-specific ASSIGNEE_BOUNCE row), so
 * a single failed email shows as two issues. The dedicated ASSIGNEE_BOUNCE row
 * is the one to keep (it carries the "update the participant's email" action),
 * so `collectSentItemIssues` suppresses the SENT_ITEM twin using these helpers.
 *
 * The key set is built **ignore-agnostic**: retrying/ignoring a bounce flips the
 * ASSIGNEE_BOUNCED entry's `ignore` but the record still exists, and we must not
 * let the SENT_ITEM twin reappear once the ASSIGNEE_BOUNCE row is resolved.
 * Because we only suppress when a matching record EXISTS, a bounced message with
 * no assignee-bounce record (e.g. legacy data) still surfaces.
 */
export const buildAssigneeBounceItemKeys = (
  instanceErrors: IExperience['instanceErrors']
): Set<string> => {
  const keys = new Set<string>();
  instanceErrors.forEach((e) => {
    if (e.type === ErroDataType.ASSIGNEE_BOUNCED) {
      const b = e as AssigneeBouncedErrorData;
      keys.add(`${b.itemType}:${b.itemTplId}`);
    }
  });
  return keys;
};

/**
 * True when a bounced message / task-start send is already represented by an
 * ASSIGNEE_BOUNCE row (per `buildAssigneeBounceItemKeys`). A message keys off
 * `scope.msgId`; a task-start can carry several task ids (combined sends) and
 * matches if ANY of them has a bounce record.
 */
export const messageCoveredByAssigneeBounce = (
  msg: ConcreteExperienceMessage,
  keys: Set<string>
): boolean => {
  if (msg.type === 'EXPERIENCE_MESSAGE') {
    return keys.has(`MESSAGE:${msg.scope?.msgId ?? ''}`);
  }
  if (msg.type === 'TASK_START') {
    return (msg.scope?.taskIds ?? []).some((id) => keys.has(`TASK:${id}`));
  }
  return false;
};
